/**
 * MapManager - OpenLayers 지도 인스턴스 관리
 */

import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { Style, Circle as CircleStyle, Fill, Stroke } from 'ol/style';
import { fromLonLat, toLonLat } from 'ol/proj';
import Control from 'ol/control/Control';
import { defaults as defaultControls, Attribution } from 'ol/control';
import { eventBus, Events } from '../utils/EventBus.js';
import { MapScaleBar } from '../ui/MapScaleBar.js';
import { getBasemapCatalog, findBasemap, BASEMAP_GROUPS, DEFAULT_BASEMAP } from './basemaps.js';

/**
 * 나침반 컨트롤 - 지도 회전 표시 및 정북 복귀
 */
class CompassControl extends Control {
  constructor() {
    const button = document.createElement('button');
    button.type = 'button';
    button.title = '회전 조절 (클릭)';
    button.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="9"/>
        <polygon points="12,4 9,13 12,11 15,13" fill="#e60012" stroke="#e60012"/>
        <polygon points="12,20 9,11 12,13 15,11" fill="currentColor"/>
      </svg>
    `;

    // 회전 조절 슬라이더 팝업 (0~180°)
    const panel = document.createElement('div');
    panel.className = 'egis-compass-panel';
    panel.hidden = true;
    panel.innerHTML = `
      <input type="range" class="egis-compass-slider" min="0" max="360" step="1" value="0"
             aria-label="지도 회전 각도">
      <div class="egis-compass-panel-row">
        <span class="egis-compass-num-wrap">
          <input type="number" class="egis-compass-num" min="0" max="360" step="1" value="0"
                 aria-label="지도 회전 각도(숫자 입력)">°
        </span>
        <button type="button" class="egis-compass-reset">정북</button>
      </div>
    `;

    const element = document.createElement('div');
    element.className = 'ol-control egis-compass';
    element.appendChild(button);
    element.appendChild(panel);

    super({ element });

    this.button = button;
    this.panel = panel;
    this.iconEl = button.querySelector('svg');
    this.slider = panel.querySelector('.egis-compass-slider');
    this.numInput = panel.querySelector('.egis-compass-num');
    this.resetBtn = panel.querySelector('.egis-compass-reset');

    button.addEventListener('click', (e) => {
      e.stopPropagation();
      this.togglePanel();
    });

    // 슬라이더 조작 → 지도 회전
    this.slider.addEventListener('input', () => {
      this.applyDegrees(Number(this.slider.value), { fromSlider: true });
    });

    // 숫자 입력 → 지도 회전
    this.numInput.addEventListener('input', () => {
      const raw = Number(this.numInput.value);
      if (Number.isNaN(raw)) return;
      this.applyDegrees(raw, { fromNumber: true });
    });
    // 입력 확정 시 0~180 범위로 표시값 정규화
    this.numInput.addEventListener('change', () => {
      const d = this.clampDeg(Number(this.numInput.value) || 0);
      this.numInput.value = String(d);
    });

    this.resetBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.getMap()?.getView().animate({ rotation: 0, duration: 300 });
    });

    // 패널 바깥을 누르면 닫기
    this._onOutside = (e) => {
      if (!this.element.contains(e.target)) this.closePanel();
    };
  }

  setMap(map) {
    super.setMap(map);
    if (map) {
      this.rotationListener = map.getView().on('change:rotation', () => this.updateRotation());
      this.updateRotation();
    }
  }

  togglePanel() {
    if (this.panel.hidden) this.openPanel();
    else this.closePanel();
  }

  openPanel() {
    this.syncSlider();
    this.panel.hidden = false;
    document.addEventListener('pointerdown', this._onOutside);
  }

  closePanel() {
    this.panel.hidden = true;
    document.removeEventListener('pointerdown', this._onOutside);
  }

  clampDeg(deg) {
    return Math.max(0, Math.min(360, Math.round(deg)));
  }

  /** 0~360°를 지도 회전에 적용하고, 입력하지 않은 쪽 컨트롤을 동기화 */
  applyDegrees(deg, opts = {}) {
    const d = this.clampDeg(deg);
    this._applying = true;
    this.getMap()?.getView().setRotation((d * Math.PI) / 180);
    this._applying = false;
    if (!opts.fromSlider) this.slider.value = String(d);
    if (!opts.fromNumber) this.numInput.value = String(d);
    this.updateSliderFill();
  }

  /** 슬라이더 값에 맞춰 진행 채움(왼쪽=채움, 오른쪽=빈 트랙)을 갱신 */
  updateSliderFill() {
    const max = Number(this.slider.max) || 360;
    const pct = max ? (Number(this.slider.value) / max) * 100 : 0;
    // 트랙 의사요소가 읽는 CSS 변수만 갱신 (요소 배경/트랙 좌표 불일치 방지)
    this.slider.style.setProperty('--fill', `${pct}%`);
  }

  /** 현재 지도 회전값을 0~180° 컨트롤에 반영 */
  syncSlider() {
    const rotation = this.getMap()?.getView().getRotation() || 0;
    const deg = this.rotationToDeg(rotation);
    this.slider.value = String(deg);
    this.numInput.value = String(deg);
    this.updateSliderFill();
  }

  rotationToDeg(rotation) {
    return (((Math.round((rotation * 180) / Math.PI)) % 360) + 360) % 360;
  }

  updateRotation() {
    const rotation = this.getMap()?.getView().getRotation() || 0;
    if (this.iconEl) {
      this.iconEl.style.transform = `rotate(${-rotation}rad)`;
    }
    const deg = this.rotationToDeg(rotation);
    this.button.title = deg === 0 ? '회전 조절 (클릭)' : `회전 ${deg}° · 클릭하여 조절`;
    // 외부 회전(정북 버튼·Alt+Shift 드래그)으로 바뀐 경우에만 컨트롤 동기화.
    // 슬라이더/숫자 입력 중(_applying)에는 사용자가 입력한 값을 덮어쓰지 않는다.
    if (!this.panel.hidden && !this._applying) {
      this.slider.value = String(deg);
      this.numInput.value = String(deg);
    }
    this.updateSliderFill();
  }
}

/**
 * GPS 위치 이동 컨트롤
 */
class GeolocateControl extends Control {
  constructor() {
    const button = document.createElement('button');
    button.type = 'button';
    button.title = '내 위치로 이동';
    button.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <line x1="12" y1="2" x2="12" y2="5"/>
        <line x1="12" y1="19" x2="12" y2="22"/>
        <line x1="2" y1="12" x2="5" y2="12"/>
        <line x1="19" y1="12" x2="22" y2="12"/>
      </svg>
    `;
    const element = document.createElement('div');
    element.className = 'ol-control egis-geolocate';
    element.appendChild(button);

    super({ element });

    this.button = button;
    this.locationLayer = null;
    this.locationFeature = null;

    button.addEventListener('click', () => this.handleClick());
  }

  ensureLayer() {
    if (this.locationLayer) return;
    this.locationFeature = new Feature();
    const source = new VectorSource({ features: [this.locationFeature] });
    this.locationLayer = new VectorLayer({
      source,
      style: new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: 'rgba(0, 102, 204, 0.9)' }),
          stroke: new Stroke({ color: '#ffffff', width: 3 })
        })
      }),
      properties: { name: 'my-location', type: 'system' },
      zIndex: 9999
    });
    this.getMap().addLayer(this.locationLayer);
  }

  /** 지금 내 위치 표시가 지도에 있는가 */
  isShowingLocation() {
    return Boolean(this.locationLayer);
  }

  /**
   * 내 위치 표시를 지운다.
   *
   * 레이어를 통째로 걷어낸다 — 도형만 비우면 빈 시스템 레이어가 지도에 남는다.
   */
  clearLocation() {
    if (!this.locationLayer) return;
    this.getMap()?.removeLayer(this.locationLayer);
    this.locationLayer = null;
    this.locationFeature = null;
    this.button.classList.remove('active');
    this.button.title = '내 위치로 이동';
  }

  handleClick() {
    // 한 번 더 누르면 표시를 지운다 — 새로고침해야 사라지던 것을 고친다
    if (this.isShowingLocation()) {
      this.clearLocation();
      return;
    }

    if (!navigator.geolocation) {
      alert('이 브라우저에서는 위치 서비스를 지원하지 않습니다.');
      return;
    }

    if (this.button.classList.contains('loading')) return;   // 연타 방지
    this.button.classList.add('loading');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.button.classList.remove('loading');
        const { longitude, latitude } = pos.coords;
        this.ensureLayer();
        const coord = fromLonLat([longitude, latitude]);
        this.locationFeature.setGeometry(new Point(coord));
        this.button.classList.add('active');
        this.button.title = '내 위치 표시 지우기';
        this.getMap().getView().animate({
          center: coord,
          zoom: Math.max(this.getMap().getView().getZoom(), 14),
          duration: 500
        });
      },
      (err) => {
        this.button.classList.remove('loading');
        const messages = {
          1: '위치 권한이 거부되었습니다. 브라우저 설정에서 허용해주세요.',
          2: '위치를 확인할 수 없습니다.',
          3: '위치 요청이 시간 초과되었습니다.'
        };
        alert(messages[err.code] || '위치를 가져올 수 없습니다.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }
}

/**
 * 배경지도 선택 컨트롤 - 한국/세계 탭 + 그 묶음의 지도 목록
 * (휴대폰에서 두 묶음을 한꺼번에 펼치면 팝업이 화면 아래로 넘쳐 세계 지도를 고를 수 없었다 — 2026-09-27)
 */
class BasemapControl extends Control {
  constructor(mapManager) {
    const button = document.createElement('button');
    button.type = 'button';
    button.title = '배경지도 선택';
    button.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
        <line x1="8" y1="2" x2="8" y2="18"/>
        <line x1="16" y1="6" x2="16" y2="22"/>
      </svg>
    `;

    const panel = document.createElement('div');
    panel.className = 'egis-basemap-panel';
    panel.hidden = true;
    panel.innerHTML = BasemapControl.panelHTML();

    const element = document.createElement('div');
    element.className = 'ol-control egis-basemap';
    element.appendChild(button);
    element.appendChild(panel);

    super({ element });

    this.mapManager = mapManager;
    this.button = button;
    this.panel = panel;
    this.element = element;

    button.addEventListener('click', (e) => {
      e.stopPropagation();
      this.togglePanel();
    });

    panel.querySelectorAll('.egis-basemap-tab').forEach((tab) => {
      tab.addEventListener('click', (e) => {
        e.stopPropagation();
        this.showGroup(tab.dataset.group);
      });
    });

    panel.querySelectorAll('.egis-basemap-option').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.select(btn.dataset.key);
      });
    });

    // 바깥 영역 클릭 시 닫기
    document.addEventListener('click', (e) => {
      if (!element.contains(e.target)) this.closePanel();
    });

    this.updateActive();
  }

  /** 묶음 탭(한국/세계) + 묶음별 항목 목록. 키가 없어 한국 묶음이 비면 탭 없이 세계 목록만 그린다. */
  static panelHTML() {
    const catalog = getBasemapCatalog();
    const groups = BASEMAP_GROUPS
      .map((group) => ({ ...group, items: catalog.filter((b) => b.group === group.id) }))
      .filter((group) => group.items.length);
    const tabs = groups.length > 1
      ? `<div class="egis-basemap-tabs" role="tablist">${groups.map((g) =>
          `<button type="button" class="egis-basemap-tab" role="tab" data-group="${g.id}">${g.tabLabel || g.label}</button>`
        ).join('')}</div>`
      : '';
    return tabs + groups.map((g) => `
      <div class="egis-basemap-list" data-group="${g.id}" role="tabpanel">
        ${g.items.map((b) =>
          `<button type="button" class="egis-basemap-option" data-key="${b.key}">${b.label}</button>`
        ).join('')}
      </div>
    `).join('');
  }

  /** 한 묶음의 목록만 펼친다 */
  showGroup(groupId) {
    this.panel.querySelectorAll('.egis-basemap-tab').forEach((tab) => {
      const on = tab.dataset.group === groupId;
      tab.classList.toggle('active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    const lists = this.panel.querySelectorAll('.egis-basemap-list');
    lists.forEach((list) => { list.hidden = lists.length > 1 && list.dataset.group !== groupId; });
  }

  togglePanel() {
    this.panel.hidden = !this.panel.hidden;
    // 열 때는 지금 배경지도가 든 묶음을 펼친다
    if (!this.panel.hidden) {
      const current = findBasemap(this.mapManager.getBasemap());
      this.showGroup(current ? current.group : BASEMAP_GROUPS[0].id);
    }
  }

  closePanel() {
    this.panel.hidden = true;
  }

  select(key) {
    this.mapManager.setBasemap(key);
    this.updateActive();
    this.closePanel();
  }

  updateActive() {
    const current = this.mapManager.getBasemap();
    this.panel.querySelectorAll('.egis-basemap-option').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.key === current);
    });
    // 기본(OSM)이 아닌 배경을 쓰는 동안 버튼을 강조한다
    this.button.classList.toggle('active', current !== DEFAULT_BASEMAP);
  }
}

export class MapManager {
  constructor() {
    this.map = null;
    this.baseLayer = null;
    this.currentBasemap = 'OSM';
  }

  /**
   * 지도 초기화
   * @param {string} targetId - 지도를 표시할 DOM 요소 ID
   * @param {Object} options - 초기화 옵션
   */
  init(targetId, options = {}) {
    const {
      center = [127.5, 36.5], // 한국 중심 좌표 (경도, 위도)
      zoom = 7,
      basemap = DEFAULT_BASEMAP
    } = options;
    const initial = findBasemap(basemap) || findBasemap(DEFAULT_BASEMAP);

    // 기본 배경 레이어 생성
    this.baseLayer = new TileLayer({
      source: initial.source(),
      properties: {
        name: 'basemap',
        type: 'base'
      }
    });

    // 라벨(지명/도로명) 오버레이 레이어 — 라벨이 있는 항목(위성+라벨)에서만 표시.
    // 소스는 항목마다 다르므로 setBasemap 이 그때그때 꽂는다.
    // zIndex 0.5: 베이스맵(0) 위 · 사용자 데이터(1+) 아래에 위치
    this.referenceLayer = new TileLayer({
      source: initial.labels ? initial.labels() : null,
      visible: !!initial.labels,
      zIndex: 0.5,
      properties: {
        name: 'reference',
        type: 'base'
      }
    });

    // 배경지도 선택 버튼 — setBasemap() 이 코드로 불릴 때도 활성 표시를 맞추려고 참조를 들고 있는다
    this.basemapControl = new BasemapControl(this);

    // 지도 생성
    this.map = new Map({
      target: targetId,
      layers: [this.baseLayer, this.referenceLayer],
      view: new View({
        center: fromLonLat(center),
        zoom: zoom,
        minZoom: 2,
        maxZoom: 19
      }),
      controls: defaultControls({
        zoom: true,
        rotate: false,
        attribution: false
      }).extend([
        new Attribution({
          collapsible: true,
          collapsed: true
        }),
        new CompassControl(),
        new GeolocateControl(),
        this.basemapControl
      ])
    });

    this.currentBasemap = initial.key;

    // 자체 축척바 (드래그 가능)
    this.scaleBar = new MapScaleBar(this.map);

    // 이벤트 바인딩
    this.bindEvents();

    return this;
  }

  /**
   * 지도 이벤트 바인딩
   */
  bindEvents() {
    // 지도 이동 완료 이벤트
    this.map.on('moveend', () => {
      const view = this.map.getView();
      const center = toLonLat(view.getCenter());
      const zoom = view.getZoom();

      eventBus.emit(Events.MAP_MOVEEND, {
        center,
        zoom,
        resolution: view.getResolution()
      });
    });

    // 마우스 이동 이벤트 (좌표 표시용)
    this.map.on('pointermove', (evt) => {
      if (evt.dragging) return;

      const coords = toLonLat(evt.coordinate);
      eventBus.emit(Events.MAP_POINTER_MOVE, {
        pixel: evt.pixel,
        coordinate: evt.coordinate,
        lonLat: coords
      });
    });

    // 클릭 이벤트
    this.map.on('click', (evt) => {
      const coords = toLonLat(evt.coordinate);
      eventBus.emit(Events.MAP_CLICK, {
        pixel: evt.pixel,
        coordinate: evt.coordinate,
        lonLat: coords
      });
    });
  }

  /**
   * 지도 인스턴스 반환
   */
  getMap() {
    return this.map;
  }

  /**
   * 뷰 반환
   */
  getView() {
    return this.map?.getView();
  }

  /**
   * 현재 중심 좌표 반환 (경도, 위도)
   */
  getCenter() {
    const center = this.map.getView().getCenter();
    return toLonLat(center);
  }

  /**
   * 현재 줌 레벨 반환
   */
  getZoom() {
    return this.map.getView().getZoom();
  }

  /**
   * 중심 좌표 설정
   * @param {number[]} lonLat - [경도, 위도]
   * @param {boolean} animate - 애니메이션 여부
   */
  setCenter(lonLat, animate = true) {
    const view = this.map.getView();
    if (animate) {
      view.animate({
        center: fromLonLat(lonLat),
        duration: 300
      });
    } else {
      view.setCenter(fromLonLat(lonLat));
    }
  }

  /**
   * 줌 레벨 설정
   * @param {number} zoom - 줌 레벨
   * @param {boolean} animate - 애니메이션 여부
   */
  setZoom(zoom, animate = true) {
    const view = this.map.getView();
    if (animate) {
      view.animate({
        zoom: zoom,
        duration: 300
      });
    } else {
      view.setZoom(zoom);
    }
  }

  /**
   * 특정 범위로 이동
   * @param {number[]} extent - [minX, minY, maxX, maxY]
   * @param {Object} options - 옵션
   */
  fitExtent(extent, options = {}) {
    const { padding = [50, 50, 50, 50], duration = 500 } = options;
    this.map.getView().fit(extent, {
      padding,
      duration
    });
  }

  /**
   * 배경지도 변경
   * @param {string} basemapKey - 카탈로그 키 (basemaps.js)
   */
  setBasemap(basemapKey) {
    const item = findBasemap(basemapKey);
    if (!item) {
      console.warn(`Unknown basemap: ${basemapKey}`);
      return;
    }

    if (item.key === 'NONE') {
      this.baseLayer.setVisible(false);
    } else {
      this.baseLayer.setVisible(true);
      this.baseLayer.setSource(item.source());
    }

    // 라벨 오버레이: 항목에 labels 가 있을 때만 그 소스로 켠다
    if (this.referenceLayer) {
      if (item.labels && item.key !== 'NONE') {
        this.referenceLayer.setSource(item.labels());
        this.referenceLayer.setVisible(true);
      } else {
        this.referenceLayer.setVisible(false);
      }
    }

    this.currentBasemap = item.key;
    if (this.basemapControl) this.basemapControl.updateActive();
  }

  /**
   * 현재 배경지도 키 반환
   */
  getBasemap() {
    return this.currentBasemap;
  }

  /**
   * 사용 가능한 배경지도 목록 (카탈로그 그대로 — 3D 패널이 optgroup 을 그린다)
   */
  getAvailableBasemaps() {
    return getBasemapCatalog().map(({ key, label, group }) => ({ key, label, group }));
  }

  /**
   * 지도 크기 갱신 (컨테이너 크기 변경 시)
   */
  updateSize() {
    this.map?.updateSize();
  }

  /**
   * 현재 축척 계산
   * @returns {number} 축척 (예: 100000 = 1:100,000)
   */
  getScale() {
    const view = this.map.getView();
    const resolution = view.getResolution();
    const mpu = view.getProjection().getMetersPerUnit();
    const dpi = 96; // 기본 DPI
    const inchesPerMeter = 39.3701;

    return resolution * mpu * inchesPerMeter * dpi;
  }

  /**
   * 축척 값을 줌 레벨로 변환
   * @param {number} scale - 목표 축척 (예: 100000 = 1:100,000)
   * @returns {number|null} 줌 레벨 또는 유효하지 않은 경우 null
   */
  scaleToZoom(scale) {
    if (!this.map || scale <= 0) return null;

    const view = this.map.getView();
    const mpu = view.getProjection().getMetersPerUnit();
    const dpi = 96;
    const inchesPerMeter = 39.3701;

    // scale = resolution * mpu * inchesPerMeter * dpi
    // resolution = scale / (mpu * inchesPerMeter * dpi)
    const targetResolution = scale / (mpu * inchesPerMeter * dpi);

    // Get zoom level for this resolution
    const zoom = view.getZoomForResolution(targetResolution);

    // Clamp to min/max zoom
    const minZoom = view.getMinZoom();
    const maxZoom = view.getMaxZoom();

    return Math.max(minZoom, Math.min(maxZoom, zoom));
  }
}

// 싱글톤 인스턴스
export const mapManager = new MapManager();
